import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthRegisterResponse } from '../models/auth-register-response.model';
import { Router } from '@angular/router';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Store } from '@ngrx/store';
import { ClientActions } from '../store/client/client.actions';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private http: HttpClient = inject(HttpClient);
  private router: Router = inject(Router);
  private store: Store = inject(Store);

  navToGoogleAuth() {
    this.guessGoogleAuthUrl().subscribe({
      next: (url: string) => {
        window.location.href = url;
      },
      error: (error: HttpErrorResponse) => {
        console.error(error);
      },
    });
  }

  private guessGoogleAuthUrl(): Observable<string> {
    const url: string = `${environment.apiUrl}/auth/google`;
    return this.http
      .get<{ url: string }>(url)
      .pipe(map((response: { url: string }) => response.url));
  }

  sendGoogleAuthCode(code: string): Observable<AuthRegisterResponse> {
    const url: string = `${environment.apiUrl}/auth/google`;
    return this.http.post<AuthRegisterResponse>(url, { code });
  }

  login(AuthRegister: AuthRegisterResponse) {
    this.setStorageAccessToken(AuthRegister.token, AuthRegister.ref_token);
    this.router.navigate(['/dashboard']);
  }

  /**
   * Connexion automatique sans redirection
   * Utilisée lors de l'initialisation de l'application
   */
  autoLogin(AuthRegister: AuthRegisterResponse): void {
    this.setStorageAccessToken(AuthRegister.token, AuthRegister.ref_token);
  }

  logout(): void {
    this.store.dispatch(ClientActions.logoutClient());
    localStorage.clear();
    this.router.navigate(['/']);
  }

  deleteAccount(): Observable<any> {
    const url: string = `${environment.apiUrl}/me`;
    return this.http.delete(url);
  }

  /**
   * Permet d'avoir un nouveau token (et refresh token) en echange d'un refresh token.
   * @param refreshToken
   * @returns
   * "user": number,
   * "token": string,
   * "ref_token": string
   *
   */
  async refresh(refreshToken: string): Promise<AuthRegisterResponse> {
    const url: string = `${environment.apiUrl}/auth/refresh`;
    return firstValueFrom(
      this.http.get<AuthRegisterResponse>(url, {
        params: {
          ref_token: refreshToken,
        },
      })
    );
  }

  checkToken(token: string): boolean {
    if (!token || token.split('.').length !== 3) {
      console.error('Token invalide:', token);
      return false;
    }
    try {
      const now: number = Math.floor(Date.now() / 1000); // timestamp en sec
      const decodeJwt: JwtPayload = jwtDecode(token);
      return decodeJwt.exp !== undefined && now < decodeJwt.exp;
    } catch (error) {
      console.error('Erreur lors du décodage du token:', error);
      return false;
    }
  }

  checkLogin(): void {
    const tokens: { token: string; refreshToken: string } =
      this.getStorageAccessToken();
    const now: number = new Date().getUTCMilliseconds();
    if (tokens.token !== '') {
      if (this.checkToken(tokens.token)) {
        this.router.navigate(['/dashboard/create-job']);
      } else {
        if (!this.checkToken(tokens.refreshToken)) {
          localStorage.clear();
          this.router.navigate(['/login']);
        } else {
          this.refresh(tokens.refreshToken).then(
            (response: AuthRegisterResponse) => {
              this.setStorageAccessToken(response.token, response.ref_token);
              this.router.navigate(['/dashboard/create-job']);
            }
          );
        }
      }
    }
  }

  /**
   * stoke les tokens dans le local storage.
   * @param token
   * @param refreshToken
   */
  setStorageAccessToken(token: string, refreshToken: string): void {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
  }

  /**
   * Recupere les tokens du local storage.
   * @returns
   */
  getStorageAccessToken(): { token: string; refreshToken: string } {
    const token: string = localStorage.getItem('token') || '';
    const refreshToken: string = localStorage.getItem('refreshToken') || '';
    return { token, refreshToken };
  }
}
